import React, { createContext, useContext } from 'react';
import type { StockAvailabilityMap } from '../hooks/use-batch-product-stock-availability.hook';

/**
 * Context to provide stock availability data to all cart components
 * without prop drilling through multiple levels
 */
const StockAvailabilityContext = createContext<StockAvailabilityMap | undefined>(undefined);

/**
 * Provider props type
 */
export type StockAvailabilityProviderProps = {
    value: StockAvailabilityMap;
    children: React.ReactNode;
};

/**
 * Context value type
 */
export type StockAvailabilityContextValue = StockAvailabilityMap;

/**
 * Provider component to wrap cart components tree
 */
export const StockAvailabilityProvider: React.FC<StockAvailabilityProviderProps> = ({ value, children }) => {
    return <StockAvailabilityContext.Provider value={value}>{children}</StockAvailabilityContext.Provider>;
};

/**
 * Hook to access stock availability data from context
 *
 * @throws Error if used outside of StockAvailabilityProvider
 * @returns Stock availability map with detailed info for each SKU
 */
export const useStockAvailabilityContext = (): StockAvailabilityMap => {
    const context = useContext(StockAvailabilityContext);
    if (context === undefined) {
        throw new Error('useStockAvailabilityContext must be used within a StockAvailabilityProvider');
    }
    return context;
};

/**
 * Hook to get stock availability for a specific SKU
 *
 * @param sku - Product SKU to check stock for
 * @returns Stock availability info for the SKU with optimistic defaults
 */
export const useStockAvailability = (
    sku: string | undefined
): {
    isSalable: boolean;
    availableQuantity: number | null;
    loading: boolean;
    errors: Array<{ code: string; message: string }> | null;
} => {
    const stockMap = useStockAvailabilityContext();

    // Return optimistic defaults if no SKU
    if (!sku) {
        return {
            isSalable: true,
            availableQuantity: null,
            loading: false,
            errors: null,
        };
    }

    // Get stock data for this SKU, default to optimistic if not found
    const stockData = stockMap[sku];
    if (!stockData) {
        return {
            isSalable: true, // Optimistic: assume available if no data
            availableQuantity: null,
            loading: false,
            errors: null,
        };
    }

    return stockData;
};
